import { ArrowLeft, Heart, Info } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  ReferenceLine,
} from "recharts";

const weeklyData = [
  { day: "Mon", delta: 2 },
  { day: "Tue", delta: 3 },
  { day: "Wed", delta: 1 },
  { day: "Thu", delta: 4 },
  { day: "Fri", delta: 6 },
  { day: "Sat", delta: 3 },
  { day: "Sun", delta: 5 },
];

const RestingHRDelta = () => {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md space-y-4 p-4">
        <div className="flex items-center gap-3 pt-2">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness2/inputs")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <h1 className="text-xl font-bold text-foreground">
            Resting HR Delta
          </h1>
        </div>

        {/* Current Value */}
        <Card className="rounded-2xl border-0 p-5 shadow-sm">
          <div className="flex items-center gap-4">
            <div className="flex h-14 w-14 items-center justify-center rounded-xl bg-destructive/10">
              <Heart className="h-7 w-7 text-destructive" />
            </div>
            <div className="flex-1">
              <p className="mb-1 text-sm text-muted-foreground">
                Current Delta
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-black text-foreground">+5</span>
                <span className="text-lg text-muted-foreground">bpm</span>
              </div>
            </div>
            <Badge className="rounded-full border-0 bg-destructive/15 text-xs text-destructive">
              Elevated
            </Badge>
          </div>
        </Card>

        {/* Impact */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Sympathetic Activation Level
          </h3>
          <Progress value={65} className="mb-2 h-3" />
          <div className="flex justify-between text-[10px] text-muted-foreground">
            <span>Normal</span>
            <span>Mildly elevated</span>
            <span>Elevated</span>
          </div>
        </Card>

        {/* Weekly Trend */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 text-sm font-semibold text-foreground">
            Weekly Resting HR Delta
          </h3>
          <div className="h-48">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={weeklyData}>
                <CartesianGrid
                  strokeDasharray="3 3"
                  vertical={false}
                  stroke="hsl(var(--border))"
                />
                <XAxis
                  dataKey="day"
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <YAxis
                  domain={[-2, 8]}
                  tick={{ fontSize: 11 }}
                  stroke="hsl(var(--muted-foreground))"
                />
                <Tooltip
                  contentStyle={{
                    borderRadius: 12,
                    border: "none",
                    boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                  }}
                  formatter={(value: number) => [`+${value} bpm`, "Delta"]}
                />
                <ReferenceLine
                  y={0}
                  stroke="hsl(var(--primary))"
                  strokeDasharray="4 4"
                />
                <Line
                  type="monotone"
                  dataKey="delta"
                  stroke="hsl(var(--destructive))"
                  strokeWidth={2}
                  dot={{ r: 4 }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* What This Means */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <div className="mb-3 flex items-center gap-2">
            <Info className="text-info h-5 w-5" />
            <h3 className="font-semibold text-foreground">What This Means</h3>
          </div>
          <div className="space-y-3 text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">
                Resting heart rate delta
              </strong>{" "}
              is the difference between your current resting HR and your
              personal baseline. A positive delta indicates sympathetic nervous
              system activation.
            </p>
            <p>
              <strong className="text-foreground">
                At +5 bpm above baseline
              </strong>
              , your body is in a mildly elevated stress state. This can be
              caused by inadequate recovery, dehydration, illness, or emotional
              stress.
            </p>
            <p>
              <strong className="text-foreground">Metabolic impact:</strong>{" "}
              Elevated resting HR is linked to increased cortisol, which can
              impair insulin sensitivity and affect how your body processes
              meals.
            </p>
          </div>
        </Card>

        {/* Tips */}
        <Card className="rounded-xl border-0 p-4 shadow-sm">
          <h3 className="mb-3 font-semibold text-foreground">How to Improve</h3>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-start gap-2">
              <span className="text-destructive">•</span>
              <span>Stay hydrated — dehydration raises resting HR</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-destructive">•</span>
              <span>Practice box breathing (4-4-4-4 pattern)</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-destructive">•</span>
              <span>Reduce training intensity for 24-48 hours</span>
            </li>
            <li className="flex items-start gap-2">
              <span className="text-destructive">•</span>
              <span>Check for signs of overtraining or illness</span>
            </li>
          </ul>
        </Card>
      </div>
    </div>
  );
};

export default RestingHRDelta;
